import { useState, useMemo } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Plus, Loader2, FolderPlus, Clock, Users, Folder } from 'lucide-react';
import { DraggableBottomSheet } from '@/components/ui/DraggableBottomSheet';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useCollections, Collection } from '@/hooks/useCollections';
import { CreateCollectionSheet } from './CreateCollectionSheet';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

const LAST_COLLECTION_KEY = 'seyu_last_collection_id';

interface AddToCollectionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  savedItemId?: string;
  savedItemIds?: string[];
  itemName?: string;
}

const colorClasses: Record<string, string> = {
  primary: 'bg-primary',
  red: 'bg-red-500',
  orange: 'bg-orange-500',
  yellow: 'bg-yellow-500',
  green: 'bg-green-500',
  blue: 'bg-blue-500',
  purple: 'bg-purple-500',
  pink: 'bg-pink-500',
};

export function AddToCollectionSheet({ 
  open, 
  onOpenChange, 
  savedItemId,
  savedItemIds,
  itemName 
}: AddToCollectionSheetProps) {
  const [addingTo, setAddingTo] = useState<string | null>(null);
  const [showCreateCollection, setShowCreateCollection] = useState(false);
  // Capture item IDs when opening create sheet to prevent re-render issues
  const [createSheetItemIds, setCreateSheetItemIds] = useState<string[]>([]);
  // Hide this sheet when CreateCollectionSheet is in crop mode
  const [isCropMode, setIsCropMode] = useState(false);
  
  const { user } = useAuth();
  const { collections, isLoading, addItemToCollection } = useCollections();

  // Fetch collaborative collections the user follows (but doesn't own)
  const { data: followedCollections = [], isLoading: isLoadingFollowed } = useQuery({
    queryKey: ['followed-collaborative-collections', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('collection_follows')
        .select('collection_id, collections!inner(id, name, color, icon, is_collaborative, user_id)')
        .eq('follower_id', user.id);
      
      if (error) throw error;
      
      return (data || [])
        .filter((row: any) => row.collections?.is_collaborative && row.collections?.user_id !== user.id)
        .map((row: any) => ({
          id: row.collections.id,
          name: row.collections.name,
          color: row.collections.color || 'primary',
          icon: row.collections.icon || '📁',
          is_collaborative: true,
          user_id: row.collections.user_id,
        } as Collection));
    },
    enabled: !!user && open,
    staleTime: 1000 * 60 * 5,
  });

  // Combine own collections + followed collaborative collections
  const allCollections = useMemo(() => {
    const followedIds = new Set(followedCollections.map(c => c.id));
    // Avoid duplicates (in case user owns a collaborative collection)
    const ownFiltered = collections.filter(c => !followedIds.has(c.id));
    return [...ownFiltered, ...followedCollections];
  }, [collections, followedCollections]);
  
  const combinedLoading = isLoading || isLoadingFollowed;

  // Get last used collection from localStorage
  const lastCollectionId = useMemo(() => {
    try {
      return localStorage.getItem(LAST_COLLECTION_KEY);
    } catch {
      return null;
    }
  }, [open]); // Re-check when sheet opens
  
  const lastCollection = useMemo(() => {
    if (!lastCollectionId) return null;
    return allCollections.find(c => c.id === lastCollectionId) || null;
  }, [lastCollectionId, allCollections]);
  
  // Filter out last collection from main list to avoid duplication
  const otherCollections = useMemo(() => {
    if (!lastCollectionId) return allCollections;
    return allCollections.filter(c => c.id !== lastCollectionId);
  }, [allCollections, lastCollectionId]);

  // Support both single item and multiple items
  const itemsToAdd = savedItemIds || (savedItemId ? [savedItemId] : []);
  const isBatch = itemsToAdd.length > 1;

  const handleOpenCreateSheet = () => {
    // Capture the current item IDs before opening
    setCreateSheetItemIds([...itemsToAdd]);
    setShowCreateCollection(true);
  };

  // Don't render the sheet when in crop mode
  const effectiveOpen = open && !isCropMode;

  const handleAddToCollection = async (collection: Collection) => {
    if (itemsToAdd.length === 0) return;
    
    setAddingTo(collection.id);
    try {
      // Add all items in parallel
      await Promise.all(
        itemsToAdd.map(itemId =>
          addItemToCollection.mutateAsync({
            collectionId: collection.id,
            savedItemId: itemId,
          }).catch(error => {
            // Ignore duplicate errors for batch operations
            if (error?.code !== '23505') throw error;
          })
        )
      );
      
      // Save as last used collection
      try {
        localStorage.setItem(LAST_COLLECTION_KEY, collection.id);
      } catch {}

      onOpenChange(false);
    } catch (error: any) {
      console.error('Add to collection error:', error);
      toast.error('Failed to add items to collection');
    } finally {
      setAddingTo(null);
    }
  };


  return (
    <>
      <AnimatePresence>
        {effectiveOpen && (
          <DraggableBottomSheet
            open={effectiveOpen}
            onOpenChange={onOpenChange}
            title={
              <div>
                <span className="text-xl font-semibold">Add to Collection</span>
                {isBatch ? (
                  <p className="text-sm text-muted-foreground mt-1 font-normal">
                    {itemsToAdd.length} items selected
                  </p>
                ) : itemName ? (
                  <p className="text-sm text-muted-foreground mt-1 font-normal">
                    {itemName}
                  </p>
                ) : null}
              </div>
            }
            height="70vh"
          >
            <div className="space-y-3 px-5 pb-8 overflow-y-auto h-full">
              {/* Create New Collection */}
              <button
                onClick={handleOpenCreateSheet}
                className="w-full flex items-center gap-3 p-4 rounded-2xl border-2 border-dashed border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                  <FolderPlus className="w-6 h-6 text-primary" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-foreground">New Collection</p>
                  <p className="text-sm text-muted-foreground">Create a new collection</p>
                </div>
              </button>

              {/* Last Used Collection - Quick Add */}
              {lastCollection && (
                <button
                  onClick={() => handleAddToCollection(lastCollection)}
                  disabled={addingTo === lastCollection.id}
                  className="w-full flex items-center gap-3 p-4 rounded-2xl border-2 border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors disabled:opacity-50"
                >
                  <div 
                    className={cn(
                      "w-12 h-12 rounded-xl flex items-center justify-center relative",
                      colorClasses[lastCollection.color] || 'bg-primary'
                    )}
                  >
                    {addingTo === lastCollection.id ? (
                      <Loader2 className="w-5 h-5 text-white animate-spin" />
                    ) : (
                      <Folder className="w-5 h-5 text-white" />
                    )}
                    <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                      <Clock className="w-2.5 h-2.5 text-white" />
                    </div>
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-foreground">{lastCollection.name}</p>
                    <p className="text-xs text-primary">Recently used</p>
                  </div>
                  <Plus className="w-5 h-5 text-primary" />
                </button>
              )}

              {/* Existing Collections */}
              {combinedLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : otherCollections.length === 0 && !lastCollection ? (
                <p className="text-center text-muted-foreground py-6">
                  No collections yet. Create one to get started!
                </p>
              ) : (
                otherCollections.map((collection) => (
                  <button
                    key={collection.id}
                    onClick={() => handleAddToCollection(collection)}
                    disabled={addingTo === collection.id}
                    className="w-full flex items-center gap-3 p-4 rounded-2xl border border-border bg-surface-2 hover:bg-surface-3 transition-colors disabled:opacity-50"
                  >
                    <div 
                      className={cn(
                        "w-12 h-12 rounded-xl flex items-center justify-center relative",
                        colorClasses[collection.color] || 'bg-primary'
                      )}
                    >
                      {addingTo === collection.id ? (
                        <Loader2 className="w-5 h-5 text-white animate-spin" />
                      ) : (
                        <Folder className="w-5 h-5 text-white" />
                      )}
                      {collection.is_collaborative && collection.user_id !== user?.id && (
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-accent flex items-center justify-center">
                          <Users className="w-2.5 h-2.5 text-accent-foreground" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-medium text-foreground">{collection.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {collection.is_collaborative && collection.user_id !== user?.id
                          ? 'Group collection'
                          : `${collection.item_count || 0} items`}
                      </p>
                    </div>
                    <Plus className="w-5 h-5 text-muted-foreground" />
                  </button>
                ))
              )}
            </div>
          </DraggableBottomSheet>
        )}
      </AnimatePresence>

      <CreateCollectionSheet
        open={showCreateCollection}
        onOpenChange={(open) => {
          setShowCreateCollection(open);
          if (!open) {
            setCreateSheetItemIds([]);
            setIsCropMode(false);
          }
        }}
        initialItemIds={createSheetItemIds}
        onCropModeChange={setIsCropMode}
        onCreated={() => {
          // Close the add-to-collection sheet too when a new collection is created with items
          setShowCreateCollection(false);
          setCreateSheetItemIds([]);
          setIsCropMode(false);
          onOpenChange(false);
        }}
      />
    </>
  );
}
